import React, { useState } from 'react';
import { PageHero } from '../components/PageHero';
import { SEO } from '../components/SEO';
import { lookupOrder } from '../api/hpanel';
import { getTrackingUrl, getCarrierDisplayName } from '../config/trackingUrls';
import { Package, ExternalLink, Loader } from 'lucide-react';

type LookupState = 'idle' | 'loading' | 'found' | 'not_found' | 'error';

export const TrackOrder = () => {
  const [orderId, setOrderId] = useState('');
  const [email, setEmail] = useState('');
  const [state, setState] = useState<LookupState>('idle');
  const [result, setResult] = useState<{
    orderId?: string;
    status?: string;
    trackingNumber?: string;
    trackingUrl?: string;
    carrier?: string;
    message?: string;
  } | null>(null);
  const [errorMessage, setErrorMessage] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!orderId.trim() || !email.trim()) return;
    setState('loading');
    setResult(null);
    setErrorMessage('');
    try {
      const data = await lookupOrder(orderId.trim(), email.trim());
      if (data.found) {
        setResult({
          orderId: data.orderId,
          status: data.status,
          trackingNumber: data.trackingNumber,
          trackingUrl: data.trackingUrl,
          carrier: data.carrier,
          message: data.message,
        });
        setState('found');
      } else {
        setResult({ message: data.message || 'Order not found.' });
        setState('not_found');
      }
    } catch (e) {
      setState('error');
      setErrorMessage(e instanceof Error ? e.message : 'Unable to look up order. Try again later.');
    }
  };

  return (
    <div className="pb-20">
      <SEO
        title="Track Your Order"
        description="Track your Simba Herbals UK order. Enter your order ID and email to see shipping status and tracking details."
        canonical="/track-order"
      />
      <PageHero
        title="Track your order"
        subtitle="Enter your order reference and email to see status and tracking."
        image="https://images.unsplash.com/photo-1566576912321-d58ddd7a6088?auto=format&fit=crop&q=80&w=2000"
        height="h-[50vh]"
      />

      <div className="max-w-lg mx-auto px-6 lg:px-12 pt-12">
        <form onSubmit={handleSubmit} className="bg-white p-6 md:p-8 rounded-2xl border border-gray-100 shadow-sm space-y-4">
          <div>
            <label className="block text-sm font-bold text-simba-dark mb-1">Order reference</label>
            <input
              type="text"
              value={orderId}
              onChange={(e) => setOrderId(e.target.value)}
              placeholder="e.g. ORD-12345"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-simba-green"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-bold text-simba-dark mb-1">Email address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email you used when ordering"
              className="w-full px-4 py-3 rounded-xl border border-gray-200 focus:outline-none focus:border-simba-green"
              required
            />
          </div>
          <button
            type="submit"
            disabled={state === 'loading'}
            className="w-full bg-simba-green text-white py-4 rounded-xl font-bold hover:bg-simba-dark transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {state === 'loading' ? <Loader className="animate-spin" size={20} /> : <Package size={20} />}
            {state === 'loading' ? 'Looking up...' : 'Track order'}
          </button>
        </form>

        {state === 'error' && (
          <div className="mt-6 p-4 bg-red-50 border border-red-100 rounded-xl text-red-700 text-sm">
            {errorMessage}
          </div>
        )}

        {state === 'not_found' && result?.message && (
          <div className="mt-6 p-6 bg-amber-50 border border-amber-100 rounded-xl">
            <p className="font-bold text-simba-dark mb-1">Order not found</p>
            <p className="text-gray-600 text-sm">{result.message}</p>
            <p className="text-gray-500 text-xs mt-2">Check your order reference and email, or contact us with your receipt.</p>
          </div>
        )}

        {state === 'found' && result && (() => {
          const trackingUrl = result.trackingUrl || (result.trackingNumber && result.carrier ? getTrackingUrl(result.carrier, result.trackingNumber) : '');
          const carrierName = result.carrier ? getCarrierDisplayName(result.carrier) : result.carrier;
          return (
          <div className="mt-6 p-6 bg-green-50 border border-green-100 rounded-xl space-y-4">
            <p className="font-bold text-simba-dark flex items-center gap-2">
              <Package size={20} /> Order {result.orderId || 'found'}
            </p>
            {result.status && (
              <p className="text-sm">
                <span className="text-gray-600">Status:</span>{' '}
                <span className="font-medium capitalize">{result.status.replace('_', ' ')}</span>
              </p>
            )}
            {result.trackingNumber && (
              <div className="pt-2 border-t border-green-200">
                <p className="text-sm text-gray-600 mb-1">Tracking number</p>
                <p className="font-mono font-bold text-simba-dark">{result.trackingNumber}</p>
                {carrierName && <p className="text-xs text-gray-500 mt-0.5">Carrier: {carrierName}</p>}
              </div>
            )}
            {trackingUrl && (
              <a
                href={trackingUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 text-simba-green font-bold hover:underline"
              >
                Track on {carrierName || 'carrier'}&apos;s website <ExternalLink size={16} />
              </a>
            )}
            {result.message && !result.trackingNumber && (
              <p className="text-sm text-gray-600">{result.message}</p>
            )}
          </div>
          );
        })()}
      </div>
    </div>
  );
};
